import { takeRight } from 'lodash'

import { MeasurLocale } from '../locale/i18n'
import { blogPostUrl, staticPagePaths } from '../locale/pathing'

import { newestPostFirst } from './blog'
import { filterLocale, rawRichTextToString } from './contentful'
import {
  IContentfulBlogPost,
  IContentfulBlogPostSection,
  ContentfulMember,
  TeamMember,
} from './shared-types'
import { StaticPage } from './staticPage'

export interface BreadcrumbPart {
  internalLink: string
  text: string
}

export const breadcrumbMetadata: (
  rootUrl: string,
  parts: BreadcrumbPart[],
) => Record<string, unknown> = (rootUrl, parts) => {
  return {
    '@context': 'https://schema.org',
    '@type': 'BreadcrumbList',
    itemListElement: parts.map(({ internalLink, text }, i) => ({
      '@type': 'ListItem',
      position: i + 1,
      ...(internalLink ? { item: rootUrl + internalLink } : {}),
      name: text,
    })),
  }
}

export const organizationMetadata = {
  '@context': 'https://schema.org',
  '@type': 'Organization',
  email: 'info@measurlabs.com',
  legalName: 'Measur Oy',
  name: 'Measurlabs',
  vatID: 'FI28204611',
}

export const personMetadata = (
  person: Omit<ContentfulMember, 'node_locale'>,
) => ({
  '@type': 'Person',
  affiliation: organizationMetadata,
  honorificSuffix: person.title,
  jobtitle: person.position,
  name: person.name,
})

export const teamMemberMetadata = (teamMember: TeamMember) => ({
  ...personMetadata({ ...teamMember, name: teamMember.name.full }),
  image:
    teamMember.portrait?.gatsbyImageData.images.fallback?.src ??
    teamMember.avatar?.gatsbyImageData.images.fallback?.src,
  telephone: teamMember.customerFacingPhone,
  worksFor: organizationMetadata,
})

export const imageMetadata = (image: {
  description?: string
  url?: string
  title?: string
}) => ({
  '@type': 'ImageObject',
  description: image.description ?? '',
  name: image.title ?? '',
  url: image.url ?? '',
})

export const faqPageMetadata = (
  items: { question: string; answer: string }[],
) => ({
  '@context': 'https://schema.org',
  '@type': 'FAQPage',
  mainEntity: items.map(({ question, answer }) => ({
    '@type': 'Question',
    acceptedAnswer: {
      '@type': 'Answer',
      text: answer,
    },
    name: question,
  })),
})

export const blogPostMetadata = (blogPost: IContentfulBlogPost) => ({
  '@context': 'https://schema.org',
  '@type': 'BlogPosting',
  about:
    blogPost.metaDescription?.metaDescription ??
    blogPost.description?.description,
  abstract: blogPost.description?.description,
  author: blogPost.author ? personMetadata(blogPost.author) : undefined,
  dateCreated: blogPost.createdAt,
  dateModified: blogPost.updateDate ?? blogPost.createdAt,
  headline: blogPost.title,
  image: blogPost.heroImage ? imageMetadata(blogPost.heroImage) : '',
  inLanguage: blogPost.node_locale,
  maintainer: organizationMetadata,
  name: blogPost.metaTitle ?? blogPost.title,
  publisher: organizationMetadata,
  text: blogPost.sections
    ?.filter(section => 'content' in section)
    .reduce(
      (acc, section) =>
        [
          acc,
          rawRichTextToString(
            (section as IContentfulBlogPostSection).content?.raw ?? '',
          ),
        ].join(' '),
      '',
    ),
  thumbnailUrl: blogPost.heroImage?.url ?? '',
  url: blogPostUrl(blogPost.node_locale, blogPost.slug),
})

export const blogMetadata = (
  blogPosts: IContentfulBlogPost[],
  locale: MeasurLocale,
  metaDescription: string,
) => ({
  '@context': 'https://schema.org',
  '@type': 'Blog',
  about: metaDescription,
  blogPost: takeRight(
    newestPostFirst(filterLocale(blogPosts, locale)).map(post =>
      blogPostMetadata(post),
    ),
    20,
  ),
  maintainer: organizationMetadata,
  publisher: organizationMetadata,
  url: staticPagePaths[StaticPage.BLOG][locale],
})
